import type { IsoTimestamp } from './common.js';

export type ContactTitle = 'Mr' | 'Mrs' | 'Ms' | 'Mx' | 'Dr';

/**
 * The same vocabulary at runtime.
 *
 * ⚠️ It exists because a TITLE IS NOT A NAME and something has to be able to
 * tell them apart: `computeDisplayName` composes `[title, firstName, lastName]`,
 * so a contact with a title and no first name has a display name of "Mr" — and
 * the client portal greeted somebody with it. `src/lib/portalGreeting.ts` strips
 * these, and reads them from HERE rather than restating them, so a title added
 * later cannot become a name a client is greeted by.
 */
export const CONTACT_TITLES: readonly ContactTitle[] = ['Mr', 'Mrs', 'Ms', 'Mx', 'Dr'];

export type AccountLink = {
  accountId: string;
  jobTitle: string | null;
  isPrimary: boolean;
  receivesServiceCommunications: boolean;
  receivesBillingCommunications: boolean;
  receivesCampaignPreviews: boolean;
};

export type Birthday = {
  day: number;
  month: number;
};

// `accountIds` is denormalised from `accountLinks[].accountId` purely for query
// efficiency (Firestore cannot `where('accountLinks.accountId','==', x)` against
// an array of objects). Kept in sync with `accountLinks` inside every write
// transaction. `isPrimary` lives only on `accountLinks` -- there is no
// denormalised primary list, so there is no drift risk.
export type Contact = {
  id: string;
  firstName: string;
  lastName: string | null;
  title: ContactTitle | null;
  email: string | null;
  phone: string | null;
  // Optional avatar URL. Raw external URL (Drive, upload service, etc.) --
  // no Firebase Storage integration. UI renders an <img> when present and
  // falls back to an initials badge when null or when the image fails to
  // load. No validation beyond length + basic URL shape.
  photoUrl: string | null;
  birthday: Birthday | null;
  notes: string | null;
  // Ad Preview Stage 3 §1.1 + Multi-Location Amendment §A5.2 — contact-level
  // channel opt-ins. A contact only receives email previews if BOTH
  // `channelEmail === true` AND (per-account-link) `receivesCampaignPreviews
  // === true` AND `email` is non-empty. SMS is symmetric on `channelSMS` +
  // `phone`. Defaults to `false` for newly created contacts; legacy contacts
  // are backfilled by scripts/migrate-contact-channel-defaults.ts (defaults
  // `channelEmail: true` where `email` is set, `channelSMS: true` where
  // `phone` is set).
  channelEmail: boolean;
  channelSMS: boolean;
  // Stage 6A v3 §2.1 — unsubscribe tracking. READ-ONLY metadata describing
  // when/why the channelEmail / channelSMS flag was last flipped by an
  // unsubscribe (Twilio STOP / SendGrid unsubscribe event) or resubscribe
  // (Twilio START). The booleans above remain the live "is this channel
  // reachable" flags; these fields are the audit story behind them. All
  // default null on legacy docs (server read-fold normalises, §2.2 — no
  // migration). *FromBroadcastId attributes the unsub to the most-recent
  // broadcast send (null for preview-origin unsubs or when none within 30d).
  channelSMSUnsubscribedAt: IsoTimestamp | null;
  channelSMSUnsubscribedFromBroadcastId: string | null;
  channelSMSResubscribedAt: IsoTimestamp | null;
  channelEmailUnsubscribedAt: IsoTimestamp | null;
  channelEmailUnsubscribedFromBroadcastId: string | null;
  channelEmailResubscribedAt: IsoTimestamp | null;
  accountLinks: AccountLink[];
  accountIds: string[];
  deletedAt: IsoTimestamp | null;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
};

export type ContactInput = Omit<
  Contact,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'deletedAt'
  | 'accountIds'
  // Unsubscribe metadata is server-derived (webhook-driven), never supplied
  // by the contact create/edit forms — same posture as accountIds.
  | 'channelSMSUnsubscribedAt'
  | 'channelSMSUnsubscribedFromBroadcastId'
  | 'channelSMSResubscribedAt'
  | 'channelEmailUnsubscribedAt'
  | 'channelEmailUnsubscribedFromBroadcastId'
  | 'channelEmailResubscribedAt'
>;

export function computeDisplayName(
  contact: Pick<Contact, 'title' | 'firstName' | 'lastName'>,
): string {
  return [contact.title, contact.firstName, contact.lastName]
    .filter((part): part is string => Boolean(part))
    .join(' ');
}
